import React from "react";
import { Link } from "react-router-dom";
import PublicNav, { PublicFooter } from "@/components/PublicNav";
import { FileText, Upload, Sparkles, FileCheck2, ArrowRight, CheckCircle2, ShieldCheck, Layers, Users2, GitBranch } from "lucide-react";

const IngestChip = ({ icon: Icon, name, color }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-slate-200">
    <Icon size={14} className={color} />
    <span className="truncate">{name}</span>
  </div>
);

export default function Home() {
  return (
    <div className="min-h-screen text-slate-100 pt-14">
      <PublicNav />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 fx-grid-bg opacity-40 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full fx-glow-cyan opacity-40" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full fx-glow-violet opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="mono text-xs uppercase tracking-[0.24em] text-cyan-300 mb-5" data-testid="hero-eyebrow">Document automation for modern teams</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Turn information into <span className="fx-gradient-text">company-standard documents</span>.
            </h1>
            <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
              Upload your templates and standards once. Fexidoc transforms existing documents, rough notes and supporting information into structured, consistent and editable business documents.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="fx-btn-primary text-white font-medium px-5 py-3 rounded-lg inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Start building your workspace <ArrowRight size={16}/>
              </Link>
              <Link to="/how-it-works" className="border border-white/10 hover:border-white/20 hover:bg-white/5 text-slate-200 px-5 py-3 rounded-lg font-medium transition" data-testid="hero-cta-secondary">See how it works</Link>
            </div>
            <p className="mt-6 text-xs text-slate-500">No more copying templates. No more inconsistent formatting. No more starting from scratch.</p>
          </div>
          {/* Hero visual */}
          <div className="lg:col-span-6 relative">
            <div className="fx-panel p-6 relative">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Workflow</div>
              <div className="grid grid-cols-11 gap-3 items-center">
                <div className="col-span-4 space-y-2">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Input</div>
                  <IngestChip icon={FileText} name="Notes.txt" color="text-cyan-300"/>
                  <IngestChip icon={FileText} name="Old Procedure.docx" color="text-blue-300"/>
                  <IngestChip icon={FileText} name="Site Photo.jpg" color="text-violet-300"/>
                  <IngestChip icon={FileText} name="Brand Guide.pdf" color="text-purple-300"/>
                </div>
                <div className="col-span-3 flex flex-col items-center gap-2">
                  <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
                  <div className="rounded-xl px-3 py-2 bg-gradient-to-br from-[#1478FF] to-[#725BFF] text-white text-xs font-semibold shadow-[0_0_30px_-5px_rgba(50,213,255,0.6)] flex items-center gap-2">
                    <Sparkles size={12}/> Fexidoc
                  </div>
                  <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-violet-400/60 to-transparent" />
                </div>
                <div className="col-span-4">
                  <img src="https://customer-assets-v7afamib.emergentagent.net/job_doc-standardization/artifacts/3wetg6od_NSSWI-example.png" alt="SWI-014 Pump 204 Bearing Inspection — Northstar Engineering safe work instruction" className="rounded-md shadow-xl border border-cyan-500/30 block w-full h-auto" loading="lazy" data-testid="hero-swi-example"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="mono text-xs uppercase tracking-[0.24em] text-slate-500 mb-4">The problem</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Documentation shouldn't depend on who created it.</h2>
          </div>
          <div className="lg:col-span-7 text-slate-300 space-y-4 leading-relaxed">
            <p>When employees format documents differently, standards slowly disappear. Templates become outdated, required sections are missed and technical staff lose hours fixing formatting.</p>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              {["Reduce repetitive formatting","Reuse approved structures","Create consistent outputs","Keep documentation standards in one place"].map((t)=>(
                <div key={t} className="flex items-start gap-2 text-sm text-slate-200"><CheckCircle2 size={16} className="text-cyan-400 mt-0.5"/> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="mono text-xs uppercase tracking-[0.24em] text-cyan-300 mb-3">Capabilities</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">A structured documentation workflow, not a chatbot.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {icon: Layers, title: "Document standardisation", body: "Bring existing documents into the current company standard."},
            {icon: FileCheck2, title: "Template intelligence", body: "Reuse the organisation's approved document structures."},
            {icon: Sparkles, title: "Document generation", body: "Turn rough information into structured formal documentation."},
            {icon: Upload, title: "Multi-source input", body: "Combine notes, PDFs, Word files, images and supporting information."},
            {icon: ShieldCheck, title: "Workspace standards", body: "Centralise terminology, writing style and document requirements."},
            {icon: GitBranch, title: "Version history", body: "Retain previous document versions and template revisions."},
          ].map((f) => (
            <div key={f.title} className="fx-panel p-6 fx-hover-panel transition group">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center border border-cyan-400/20 mb-4"><f.icon size={18} className="text-cyan-300"/></div>
              <div className="font-display font-semibold text-slate-100 text-lg">{f.title}</div>
              <div className="text-slate-400 text-sm mt-2 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="mono text-xs uppercase tracking-[0.24em] text-cyan-300 mb-3">Before &amp; after</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Rough input. Structured output.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="fx-panel p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Rough notes.txt</div>
            <pre className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed font-mono">{`Pump 204 keeps overheating.
Bearing needs checking weekly.
Isolation required before inspection.
Grease type EP2.
Need PPE.
Need temperature check.`}</pre>
          </div>
          <div className="rounded-xl p-1 bg-gradient-to-br from-cyan-400/40 via-blue-500/30 to-violet-500/40" data-testid="home-after-example">
            <img src="https://customer-assets-v7afamib.emergentagent.net/job_doc-standardization/artifacts/ted7vmhv_NSPump-example.png" alt="Pump 204 Bearing Inspection — Northstar Engineering safe work instruction" className="rounded-lg block w-full h-auto" loading="lazy"/>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 py-20">
        <div className="fx-panel p-10 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full fx-glow-cyan opacity-30"/>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Create in Fexidoc. Keep working in Word.</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">Fexidoc fits into your existing systems. Every document exports as an editable Microsoft Word file.</p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/signup" className="fx-btn-primary text-white font-medium px-5 py-3 rounded-lg" data-testid="cta-signup">Start free</Link>
            <Link to="/contact" className="border border-white/10 hover:border-white/20 text-slate-200 px-5 py-3 rounded-lg" data-testid="cta-contact">Talk to sales</Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
